import type { MatrixClient } from 'matrix-js-sdk';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { removeMyPhoto, setMyPhoto, squarePhoto } from '../../lib/matrix/photos.js';
import { hapticSuccess, hapticTap, hapticWarn } from '../../lib/native.js';
import '../messaging/recovery-sheet.css';
import './photo-sheet.css';

/**
 * Folha da foto de perfil: escolhe (galeria ou câmera), confere como vai ficar
 * no círculo e só então usa. A foto atual pode ser tirada a qualquer momento.
 */
export function PhotoSheet({
  client,
  currentUrl,
  fallbackSrc,
  onClose,
}: {
  client: MatrixClient | null;
  currentUrl: string | null;
  fallbackSrc: string;
  onClose: () => void;
}) {
  const galleryRef = useRef<HTMLInputElement | null>(null);
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const [picked, setPicked] = useState<{ blob: Blob; url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(
    () => () => {
      if (picked) URL.revokeObjectURL(picked.url);
    },
    [picked],
  );

  async function choose(file: File | undefined) {
    if (!file) return;
    setError('');
    try {
      const blob = await squarePhoto(file);
      setPicked({ blob, url: URL.createObjectURL(blob) });
      hapticTap();
    } catch {
      setError('Não consegui abrir essa imagem. Tenta outra?');
      hapticWarn();
    }
  }

  async function use() {
    if (!client || !picked || busy) return;
    setBusy(true);
    setError('');
    try {
      await setMyPhoto(client, picked.blob);
      hapticSuccess();
      onClose();
    } catch {
      setError('Não consegui guardar sua foto agora. Tenta de novo?');
      hapticWarn();
      setBusy(false);
    }
  }

  async function remove() {
    if (!client || busy) return;
    setBusy(true);
    setError('');
    try {
      await removeMyPhoto(client);
      onClose();
    } catch {
      setError('Não consegui tirar a foto agora. Tenta de novo?');
      setBusy(false);
    }
  }

  const shown = picked?.url ?? currentUrl;

  return createPortal(
    <div
      className="rk-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Sua foto"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <div className="rk-sheet ph-sheet">
        <span className="rk-handle" aria-hidden="true" />
        <div className={`ph-stage${picked ? ' fresh' : ''}`} aria-hidden="true">
          <i className="ph-blob a" />
          <i className="ph-blob b" />
          <i className="ph-spark a" />
          <i className="ph-spark b" />
          <span className="ph-ring" key={shown ?? 'vazio'}>
            <span className={`ph-circle${shown ? ' photo' : ''}`}>
              <img src={shown ?? fallbackSrc} alt="" />
            </span>
          </span>
        </div>
        <div className="rk-kicker">Sua foto</div>
        <h2>
          {picked ? 'Ficou boa assim?' : currentUrl ? 'Quer trocar a foto?' : 'Coloca uma foto sua'}
        </h2>
        <p>
          {picked
            ? 'É assim que ela aparece para quem conversa com você.'
            : 'Quem conversa com você vê essa foto no lugar do personagem.'}
        </p>
        {error && <p className="rk-error">{error}</p>}
        {picked ? (
          <>
            <button className="rk-btn" onClick={() => void use()} disabled={busy || !client}>
              {busy ? 'Guardando…' : 'Usar esta foto'}
            </button>
            <button className="rk-ghost" onClick={() => setPicked(null)} disabled={busy}>
              Escolher outra
            </button>
          </>
        ) : (
          <>
            <button className="rk-btn" onClick={() => galleryRef.current?.click()} disabled={busy}>
              Escolher da galeria
            </button>
            <button
              className="rk-ghost ph-soft"
              onClick={() => cameraRef.current?.click()}
              disabled={busy}
            >
              Tirar uma foto agora
            </button>
            {currentUrl && (
              <button className="rk-ghost ph-danger" onClick={() => void remove()} disabled={busy}>
                {busy ? 'Tirando…' : 'Ficar sem foto'}
              </button>
            )}
            <button className="rk-ghost" onClick={onClose} disabled={busy}>
              Agora não
            </button>
          </>
        )}
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            event.currentTarget.value = '';
            void choose(file);
          }}
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="user"
          hidden
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            event.currentTarget.value = '';
            void choose(file);
          }}
        />
      </div>
    </div>,
    document.body,
  );
}
